import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Sparkles, ShieldCheck, ThumbsUp, X, Loader2, Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Renders 5 star icons. `value` fills stars 1..value inclusive. Non-interactive
 * unless `onChange` is provided.
 */
function StarRow({ value = 0, size = 4, onChange = null, className = "" }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`}>
      {stars.map((s) => {
        const filled = value >= s;
        const half = value >= s - 0.5 && value < s;
        const Icon = Star;
        return (
          <button
            key={s}
            type="button"
            disabled={!onChange}
            onClick={() => onChange?.(s)}
            aria-label={`${s} star${s > 1 ? "s" : ""}`}
            className={`${onChange ? "cursor-pointer hover:scale-110" : "cursor-default"} transition-transform`}
            data-testid={onChange ? `star-input-${s}` : undefined}
          >
            <Icon
              className={`w-${size} h-${size} ${filled ? "fill-champagne text-champagne" : half ? "fill-champagne/50 text-champagne" : "fill-transparent text-ink/25"}`}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </span>
  );
}

function DistributionBar({ label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="w-6 text-ink/60 font-body tabular-nums">{label}★</span>
      <div className="flex-1 h-1.5 bg-cream rounded-full overflow-hidden">
        <div className="h-full bg-champagne rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right text-ink/50 font-label text-[10px] tabular-nums">{count}</span>
    </div>
  );
}

function ReviewCard({ review, onHelpful }) {
  const [busy, setBusy] = useState(false);
  const [marked, setMarked] = useState(false);

  const handleHelpful = async () => {
    if (busy || marked) return;
    setBusy(true);
    try {
      await api.markReviewHelpful(review.id);
      setMarked(true);
      onHelpful?.(review.id);
      toast.success("Thanks — that helps other students decide.");
    } catch (e) {
      const msg = errMsg(e);
      if (msg.includes("already")) setMarked(true);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const date = new Date(review.created_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`review-card-${review.id}`}
      className="border-b border-cream py-6 last:border-b-0"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <StarRow value={review.rating} size={4} />
            <span className="font-label text-[10px] tracking-widest text-ink/50">{date}</span>
          </div>
          <h4 className="font-display text-xl leading-tight text-ink">{review.title}</h4>
        </div>
      </div>
      <p className="font-body text-[15px] text-ink/75 leading-relaxed">{review.body}</p>
      <div className="mt-4 flex items-center flex-wrap gap-x-4 gap-y-2 text-[11px] font-label tracking-widest text-ink/50">
        <span className="text-ink/70">{review.user_name}</span>
        {review.user_school_name && (
          <span className="text-ink/50">· {review.user_school_name}</span>
        )}
        {review.verified_purchase && (
          <span className="inline-flex items-center gap-1 text-rosemetal">
            <ShieldCheck className="w-3 h-3" strokeWidth={2} /> VERIFIED PURCHASE
          </span>
        )}
        {review.color && (
          <span className="text-ink/50">· Reviewed in {review.color}</span>
        )}
      </div>
      <div className="mt-3">
        <button
          onClick={handleHelpful}
          disabled={busy || marked}
          data-testid={`review-helpful-${review.id}`}
          className={`inline-flex items-center gap-1.5 text-[11px] font-label tracking-widest transition-colors ${
            marked ? "text-champagne" : "text-ink/50 hover:text-rosemetal"
          }`}
        >
          <ThumbsUp className="w-3 h-3" strokeWidth={2} />
          {marked ? "HELPFUL · " : "HELPFUL · "} {review.helpful_count + (marked ? 1 : 0)}
        </button>
      </div>
    </motion.article>
  );
}

function WriteReviewModal({ product, activeColor, onClose, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Pick a star rating.");
    if (title.trim().length < 3) return toast.error("Give your review a short title.");
    if (body.trim().length < 20) return toast.error("Add a little more detail (20+ characters).");
    setBusy(true);
    try {
      const { data } = await api.createProductReview(product.id, {
        rating,
        title: title.trim(),
        body: body.trim(),
        color: activeColor || null,
      });
      toast.success("Thank you — your review is live.");
      onSubmitted?.(data.review);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.form
        onSubmit={submit}
        initial={{ y: 20, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 20, opacity: 0, scale: 0.97 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="write-review-modal"
        className="relative bg-ivory rounded-sm max-w-lg w-full p-8 border border-champagne/40 shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="write-review-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full border border-cream hover:border-ink flex items-center justify-center text-ink/60 hover:text-ink"
        >
          <X className="w-4 h-4" />
        </button>
        <p className="font-label text-[10px] text-rosemetal tracking-[0.28em] mb-2 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> SHARE YOUR EXPERIENCE
        </p>
        <h3 className="font-display text-3xl leading-tight text-ink mb-2">Review this dress.</h3>
        <p className="font-body text-sm text-ink/60 mb-6">
          <b className="text-ink/80">{product.name}</b>
          {activeColor && <> · {activeColor}</>}
        </p>

        <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">YOUR RATING</label>
        <StarRow value={rating} size={8} onChange={setRating} className="mb-6" />

        <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">SUMMARY</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Best dress ever."
          data-testid="review-title-input"
          className="w-full bg-ivory border border-cream rounded-sm px-3.5 py-3 font-body text-[15px] outline-none focus:border-rosemetal mb-5"
        />

        <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">YOUR REVIEW</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          minLength={20}
          maxLength={3000}
          rows={6}
          placeholder="How did it fit? How did you feel wearing it? What would you tell a friend at your school?"
          data-testid="review-body-input"
          className="w-full bg-ivory border border-cream rounded-sm px-3.5 py-3 font-body text-[15px] outline-none focus:border-rosemetal resize-y min-h-[130px]"
        />
        <p className="font-label text-[9px] tracking-widest text-ink/40 mt-2">{body.length}/3000 · MIN 20</p>

        <button
          type="submit"
          disabled={busy}
          data-testid="review-submit"
          className="mt-6 w-full bg-ink text-cream py-4 font-label text-[10px] tracking-[0.3em] rounded-sm hover:bg-rosemetal transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> POSTING…</> : "POST REVIEW"}
        </button>
      </motion.form>
    </motion.div>
  );
}

export default function ProductReviews({ product, activeColor, onSummary, onReviewsLoaded }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.productReviews(product.id, 50, 0);
      setReviews(data.reviews || []);
      setSummary(data.summary || summary);
      onSummary?.(data.summary || null);
      onReviewsLoaded?.(data.reviews || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.id]);

  const openReview = () => {
    if (!user) {
      toast("Sign in to review.");
      navigate("/auth");
      return;
    }
    setModalOpen(true);
  };
  const onSubmitted = (newReview) => {
    setModalOpen(false);
    const nextReviews = [newReview, ...reviews];
    setReviews(nextReviews);
    onReviewsLoaded?.(nextReviews);
    // Nudge the aggregate live so user sees their star immediately.
    // Compute next-state via closure (not inside the updater fn) so we can
    // safely propagate to the parent without triggering a setState-during-render.
    const key = String(newReview.rating);
    const nextDist = { ...summary.distribution, [key]: (summary.distribution[key] || 0) + 1 };
    const newCount = summary.count + 1;
    const newTotal = summary.average * summary.count + newReview.rating;
    const next = {
      count: newCount,
      average: Math.round((newTotal / newCount) * 100) / 100,
      distribution: nextDist,
    };
    setSummary(next);
    onSummary?.(next);
  };

  const visibleReviews = expanded ? reviews : reviews.slice(0, 4);
  const hasMore = reviews.length > 4;

  return (
    <section id="reviews" data-testid="product-reviews" className="mt-24 border-t border-cream pt-16">
      <div className="grid lg:grid-cols-[380px_1fr] gap-10 lg:gap-16">
        {/* Summary sidebar */}
        <div className="lg:sticky lg:top-24 self-start">
          <p className="font-label text-[10px] text-rosemetal tracking-[0.32em] mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> WHAT SHE'S WEARING SAYS
          </p>
          <h2 className="font-display text-4xl md:text-5xl leading-none text-ink">Reviews</h2>

          {summary.count > 0 ? (
            <>
              <div className="mt-6 flex items-baseline gap-4">
                <span className="font-display text-6xl leading-none text-ink tabular-nums">{summary.average.toFixed(1)}</span>
                <div>
                  <StarRow value={summary.average} size={5} />
                  <p className="font-label text-[10px] text-ink/50 tracking-widest mt-1">{summary.count} REVIEW{summary.count !== 1 ? "S" : ""}</p>
                </div>
              </div>
              <div className="mt-6 space-y-1.5">
                {[5, 4, 3, 2, 1].map((r) => (
                  <DistributionBar key={r} label={r} count={summary.distribution[String(r)] || 0} total={summary.count} />
                ))}
              </div>
            </>
          ) : (
            <div className="mt-6 p-5 border border-cream rounded-sm bg-cream/30">
              <StarRow value={0} size={5} className="mb-3" />
              <p className="font-body text-sm text-ink/60 leading-relaxed">Be the first to review this dress. Your take helps every other student at your school.</p>
            </div>
          )}

          <button
            onClick={openReview}
            data-testid="write-review-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ink text-cream px-6 py-3.5 font-label text-[10px] tracking-[0.28em] rounded-sm hover:bg-rosemetal transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={2} /> WRITE A REVIEW
          </button>
        </div>

        {/* Reviews list */}
        <div>
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-rosemetal" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="border border-dashed border-cream rounded-sm p-10 text-center">
              <Star className="w-8 h-8 text-champagne/60 mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-display text-xl text-ink mb-2">No reviews yet.</p>
              <p className="font-body text-sm text-ink/60 max-w-sm mx-auto">
                Once someone from your school locks and wears this dress, their story lands here.
              </p>
            </div>
          ) : (
            <>
              <div data-testid="reviews-list">
                {visibleReviews.map((r) => (
                  <ReviewCard key={r.id} review={r} />
                ))}
              </div>
              {hasMore && (
                <button
                  onClick={() => setExpanded((x) => !x)}
                  data-testid="reviews-expand"
                  className="mt-6 font-label text-[10px] tracking-widest text-rosemetal hover:text-ink"
                >
                  {expanded ? "SHOW LESS ↑" : `SHOW ALL ${reviews.length} REVIEWS ↓`}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {modalOpen && (
          <WriteReviewModal
            product={product}
            activeColor={activeColor}
            onClose={() => setModalOpen(false)}
            onSubmitted={onSubmitted}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export { ProductReviews };
